import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Button,
  ColumnLayout,
  Container,
  Flashbar,
  Form,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { createTemplate } from 'src/api/permissions';
import { scrollUp } from '../utils/navigation';
import * as validate from '../../commons/validationUtils';
import { Page } from 'src/routes';
import {
  FORM_CONTENT_TYPE,
  TEMPLATE_TYPE_ACCESS_MANAGEMENT,
  TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
} from 'src/commons/constants';
import { isD3AServiceAvailable } from 'src/api/config';
import {
  getAccessManagementComponent,
  getAccessRequestWorkflowComponent,
  getDataAgreementOptionsForOwner,
  populateAccessManagementInfo,
  populateAccessRequestWorkflows,
  templateTypeOptions,
} from 'src/components/templates/common';
import { CommonPageProps } from 'src/components/common/CommonPageProps';

export interface CreateTemplateComponentProps extends CommonPageProps {
  toggleHelp: any;
}

export const CreateTemplateComponent = (props: CreateTemplateComponentProps) => {
  const [notifications, setNotifications] = useState([]);
  const [, setLoading] = useState(false);
  const [templateName, setTemplateName] = useState(null);
  const [description, setDescription] = useState(null);
  const [simTemplateName, setSimTemplateName] = useState(null);
  const [simTemplateLink, setSimTemplateLink] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  // common fields
  const [templateType, setTemplateType] = useState(null);
  const [approvalTemplateLink, setApprovalTemplateLink] = useState(null);
  const [approvalTemplateTitle, setApprovalTemplateTitle] = useState(null);
  const [autoApproval, setAutoApproval] = useState(false);
  const [allowMergeApprovalWorkflow, setAllowMergeApprovalWorkflow] = useState(false);
  // access request workflow fields
  const [priorityOrder, setPriorityOrder] = useState<number>(undefined);
  const [arwRuleName, setArwRuleName] = useState(null);
  const [arwRuleDesc, setArwRuleDesc] = useState(null);
  const [arwRule, setArwRule] = useState(null);
  const [arwDataAgreement, setArwDataAgreement] = useState(null);
  const [arwDataAgreementModalVisible, setArwDataAgreementModalVisible] = useState(false);
  const [arwDataAgreementId, setArwDataAgreementId] = useState(null);
  const [arwDataAgreementName, setArwDataAgreementName] = useState(null);
  const [arwDataAgreementOptions, setArwDataAgreementOptions] = useState([]);
  const [templateTypeOptionsAllowed, setTemplateTypeOptionsAllowed] = useState([]);
  const [arwDataAgreementOwnerId, setArwDataAgreementOwnerId] = useState(undefined);
  const [optimisticApprovalEnabled, setOptimisticApprovalEnabled] = useState(false);

  // Reset optimistic approval when data agreement becomes invalid
  useEffect(() => {
    if ((!arwDataAgreementId || arwDataAgreementId.length !== 36) && optimisticApprovalEnabled) {
      setOptimisticApprovalEnabled(false);
    }
  }, [arwDataAgreementId]);

  useEffect(() => {
    props.setContentType(FORM_CONTENT_TYPE);
    setTemplateTypeOptionsAllowed([
      ...templateTypeOptions.filter((option) => option.value != TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW),
    ]);
    // load data agreements
    if (isD3AServiceAvailable()) {
      setTemplateTypeOptionsAllowed(templateTypeOptions);
      const ownerIds: string[] = props.activeWorkspace
        ? [props.activeWorkspace.workspaceId, props.activeWorkspace.groupId]
        : [props.activeGroup];
      getDataAgreementOptionsForOwner(ownerIds).then((options) => setArwDataAgreementOptions(options));
    }
  }, []);

  const isAccessManagementType = (): boolean => {
    return templateType != null && templateType.value == TEMPLATE_TYPE_ACCESS_MANAGEMENT;
  };

  const isAccessRequestWorkflowType = (): boolean => {
    return templateType != null && templateType.value == TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW;
  };

  const handleConfirm = async () => {
    setLoading(true);
    let accessManagementInfo = undefined;
    let accessRequestWorkflows = undefined;
    if (isAccessManagementType()) {
      accessManagementInfo = populateAccessManagementInfo(
        simTemplateName,
        simTemplateLink,
        approvalTemplateLink,
        approvalTemplateTitle,
        autoApproval,
        allowMergeApprovalWorkflow,
      );
    }
    if (isAccessRequestWorkflowType()) {
      accessRequestWorkflows = populateAccessRequestWorkflows(
        arwRule,
        arwRuleName,
        arwRuleDesc,
        arwDataAgreementId,
        arwDataAgreementName,
        arwDataAgreementOwnerId,
        approvalTemplateLink,
        approvalTemplateTitle,
        autoApproval,
        allowMergeApprovalWorkflow,
        priorityOrder,
        optimisticApprovalEnabled,
      );
    }

    let request = {
      name: templateName,
      templateType: templateType.value,
      description: description,
      ownerId: props.activeWorkspace ? props.activeWorkspace.workspaceId : props.activeGroup,
      accessManagementInfo: accessManagementInfo,
      accessRequestWorkflows: accessRequestWorkflows,
    };
    try {
      const createResponse = await createTemplate(request);
      const status = createResponse.message == 'Success' ? 'success' : 'error';
      setButtonLoading(false);
      props.setRedirect(Page.TEMPLATES, false, {
        status,
        message: `Template created successfully with template id: ${createResponse.templateId}`,
      });
    } catch (e) {
      setNotifications([
        {
          header: 'error',
          type: 'error',
          content: e.message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
    setLoading(false);
    scrollUp();
  };

  const allFieldsSet = () => {
    let isApprovalValid = validate.isNotFalsy(approvalTemplateTitle) && validate.isNotFalsy(approvalTemplateLink);
    let isDataAgreementValid = validate.isNotFalsy(arwDataAgreementId) && arwDataAgreementId?.length == 36;
    let isSimValid = validate.isNotFalsy(simTemplateName) && validate.isNotFalsy(simTemplateLink);
    let isPriorityOrderValid = priorityOrder !== undefined && priorityOrder >= 0 && priorityOrder <= 100;
    return (
      validate.isNotFalsy(templateName) &&
      validate.isNotFalsy(description) &&
      ((isAccessManagementType() && (isSimValid || isApprovalValid)) ||
        (isAccessRequestWorkflowType() && isPriorityOrderValid && (isDataAgreementValid || isApprovalValid)))
    );
  };

  return (
    <div>
      <Flashbar items={notifications}></Flashbar>
      <Form
        header={
          <Header
            variant='h1'
            description='Please specify the details of the template.'
            info={
              <Link variant='info' onFollow={props.toggleHelp}>
                {' '}
                Info{' '}
              </Link>
            }
          >
            Create new template
          </Header>
        }
        actions={
          <SpaceBetween direction='horizontal' size='s'>
            <Button variant='link' onClick={() => props.setRedirect(Page.TEMPLATES)}>
              Cancel
            </Button>
            <Button variant='primary' loading={buttonLoading} onClick={handleConfirm} disabled={!allFieldsSet()}>
              Create template
            </Button>
          </SpaceBetween>
        }
      >
        <Container header={<Header variant={'h2'}>Template details</Header>}>
          <ColumnLayout>
            <SpaceBetween size='m'>
              <FormField label={<div>Name</div>} description='Name of the template'>
                <Input
                  name='template-details'
                  ariaRequired={true}
                  onChange={(event) => setTemplateName(event.detail.value)}
                  invalid={templateName !== null && validate.isFalsy(templateName)}
                  value={templateName}
                />
              </FormField>
              <FormField label={'Description'} description='A brief description about the template.'>
                <Textarea
                  onChange={({ detail }) => setDescription(detail.value)}
                  value={description}
                  invalid={description !== null && validate.isFalsy(description)}
                />
              </FormField>
              <FormField label={<div>Template type</div>} description='The type of template to be created.'>
                <Select
                  selectedOption={templateType}
                  onChange={({ detail }) => setTemplateType(detail.selectedOption)}
                  options={templateTypeOptionsAllowed}
                  selectedAriaLabel='Selected'
                  placeholder='Please select the template type'
                  errorText='Error fetching type.'
                  recoveryText='Retry'
                  empty={'No types available'}
                  filteringType='auto'
                  filteringAriaLabel='Filter origins'
                  ariaRequired={true}
                />
              </FormField>

              {isAccessManagementType() &&
                getAccessManagementComponent(
                  simTemplateName,
                  setSimTemplateName,
                  simTemplateLink,
                  setSimTemplateLink,
                  approvalTemplateTitle,
                  setApprovalTemplateTitle,
                  approvalTemplateLink,
                  setApprovalTemplateLink,
                  autoApproval,
                  setAutoApproval,
                  allowMergeApprovalWorkflow,
                  setAllowMergeApprovalWorkflow,
                )}

              {isAccessRequestWorkflowType() &&
                getAccessRequestWorkflowComponent(
                  arwRuleName,
                  setArwRuleName,
                  arwRuleDesc,
                  setArwRuleDesc,
                  arwRule,
                  setArwRule,
                  arwDataAgreementOptions,
                  arwDataAgreement,
                  setArwDataAgreement,
                  arwDataAgreementModalVisible,
                  setArwDataAgreementModalVisible,
                  setArwDataAgreementOwnerId,
                  arwDataAgreementId,
                  setArwDataAgreementId,
                  arwDataAgreementName,
                  setArwDataAgreementName,
                  approvalTemplateTitle,
                  setApprovalTemplateTitle,
                  approvalTemplateLink,
                  setApprovalTemplateLink,
                  autoApproval,
                  setAutoApproval,
                  allowMergeApprovalWorkflow,
                  setAllowMergeApprovalWorkflow,
                  priorityOrder,
                  setPriorityOrder,
                  optimisticApprovalEnabled,
                  setOptimisticApprovalEnabled,
                )}
            </SpaceBetween>
          </ColumnLayout>
        </Container>
      </Form>
    </div>
  );
};
